import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, CreditCard, Download, Home, X } from 'lucide-react';
import { BACKEND_BASE_URL } from '../config/api';
import { getActivePaymentSettingForUser, submitApplicationPayment, getPublicPaymentData } from '../ServiceAPI/application/api';
import paymentQrImage from '../assets/payment-qr.jpeg';

const PublicPayment = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const receiptRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [upiId, setUpiId] = useState('');
  const [paymentSetting, setPaymentSetting] = useState(null);
  const [apps, setApps] = useState([]);
  const [qrLoadError, setQrLoadError] = useState(false);
  const [useDynamicQr, setUseDynamicQr] = useState(true);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  const getErrorMessage = (err, fallback = 'Something went wrong') => {
    const direct = err?.response?.data?.error;
    if (typeof direct === 'string') return direct;
    if (direct && typeof direct === 'object' && typeof direct.message === 'string') {
      return direct.message;
    }

    const message = err?.response?.data?.message;
    if (typeof message === 'string') return message;

    if (typeof err?.message === 'string') return err.message;
    return fallback;
  };

  const getQrUrl = (qrPath) => {
    if (!qrPath) return paymentQrImage;
    const normalized = String(qrPath).replace(/\\/g, '/');
    if (/^https?:\/\//i.test(normalized)) return normalized;
    const marker = '/uploads/';
    const idx = normalized.toLowerCase().indexOf(marker);
    if (idx < 0) return paymentQrImage;
    const relative = normalized.slice(idx + 1);
    return `${BACKEND_BASE_URL}/${relative}`;
  };

  const totalAmount = useMemo(
    () => apps.reduce((sum, app) => {
      const amount = app.Effective_fee || 0;
      return sum + Number(amount);
    }, 0),
    [apps]
  );

  const upiPaymentLink = useMemo(() => {
    const payeeUpi = paymentSetting?.Upi_id?.trim();
    if (!payeeUpi) return '';

    const amount = Number(totalAmount || 0);
    if (!amount || amount <= 0) return '';

    const params = new URLSearchParams({
      pa: payeeUpi,
      pn: paymentSetting?.Account_name || 'RTO Bharat',
      am: amount.toFixed(2),
      cu: 'INR'
    });

    return `upi://pay?${params.toString()}`;
  }, [paymentSetting, totalAmount]);

  const dynamicQrUrl = useMemo(() => {
    if (!upiPaymentLink) return '';
    return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(upiPaymentLink)}`;
  }, [upiPaymentLink]);

  useEffect(() => {
    setUseDynamicQr(true);
    setQrLoadError(false);
  }, [dynamicQrUrl, paymentSetting?.Qr_image]);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        setError('');

        const [publicData, settingResp] = await Promise.all([
          getPublicPaymentData(token),
          getActivePaymentSettingForUser()
        ]);

        const application = publicData?.application || publicData?.data?.application || null;
        const paymentSettingData = publicData?.paymentSetting || publicData?.data?.paymentSetting || settingResp?.data || null;

        if (!application) {
          throw new Error(publicData?.error || 'We could not find your application details for this payment link.');
        }

        const normalizeStatus = (value) =>
          String(value || '').toLowerCase().replace(/[_-]+/g, ' ').trim();

        const eligible = ['documents verified', 'payment pending', 'payment didnt receive'].includes(
          normalizeStatus(application.Application_status || application.ApplicationStatus)
        );

        if (!eligible) {
          setError('This application is not currently eligible for payment.');
          setApps([]);
          setPaymentSetting(paymentSettingData);
          return;
        }

        setApps([application]);
        setPaymentSetting(paymentSettingData);
      } catch (err) {
        console.error('Public payment init failed:', err);
        setError(getErrorMessage(err, 'Failed to load payment details for this link.'));
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      init();
    } else {
      setError('This payment link is invalid.');
      setLoading(false);
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const upiRegex = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/;
    const normalizedUpiId = upiId.trim().toLowerCase();
    if (normalizedUpiId && !upiRegex.test(normalizedUpiId)) {
      setError('Please enter a valid UPI ID.');
      return;
    }

    const applicationIds = apps.map((app) => Number(app.Id || app.ApplicationId)).filter(Boolean);
    if (!applicationIds.length) {
      setError('No valid applications selected for payment.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitApplicationPayment({
        upiId: normalizedUpiId || null,
        applicationIds
      });

      if (!result?.success) {
        throw new Error(result?.error || 'Payment submission failed');
      }

      setReceiptData({
        transactionId: `TXN${Date.now()}`,
        timestamp: new Date(),
        payeeAccount: paymentSetting?.Account_name || 'RTO Bharat',
        payeeUpi: paymentSetting?.Upi_id,
        payerUpi: normalizedUpiId || 'Not provided',
        amount: totalAmount,
        applications: apps,
        status: 'Success'
      });

      setPaymentSuccess(true);
      window.dispatchEvent(new Event('storage'));
      window.dispatchEvent(new Event('payment-submitted'));
    } catch (err) {
      const backendError = getErrorMessage(err, 'Payment submission failed');
      if (String(backendError).toLowerCase().includes('missing fields: userid, upiid')) {
        setError('Backend is running old payment API. Please restart backend server to apply latest changes.');
      } else {
        setError(backendError);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPDF = () => {
    if (receiptRef.current) {
      window.print();
    }
  };

  return (
    <div className="min-h-[calc(100vh-70px)] bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 md:p-8">
      {paymentSuccess && receiptData ? (
        <div className="max-w-2xl mx-auto">
          <div ref={receiptRef} className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
            <div className="text-center mb-8 pb-8 border-b border-gray-200 dark:border-gray-700">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-green-400 to-green-600 text-white mb-4">
                <CheckCircle className="w-8 h-8" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Payment Successful!</h1>
              <p className="text-gray-500 dark:text-gray-400">Your payment has been submitted successfully</p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-8 pb-8 border-b border-gray-200 dark:border-gray-700">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Transaction ID</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white font-mono">{receiptData.transactionId}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Date & Time</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{receiptData.timestamp.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Payee</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{receiptData.payeeAccount}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Payee UPI</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white break-all">{receiptData.payeeUpi}</p>
              </div>
            </div>

            <div className="mb-8 pb-8 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Payment Details</h2>
              <div className="space-y-3">
                {receiptData.applications.map((app) => {
                  const totalFee = Number(app.Effective_fee || 0);

                  return (
                    <div key={app.Id || app.ApplicationId} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-white">{app.ServiceName}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Application ID: {app.Id || app.ApplicationId}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg text-green-600">INR {totalFee.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mb-8 pb-8 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center p-4 bg-gradient-to-r from-green-50 to-green-50 dark:from-gray-900 dark:to-gray-900 rounded-lg border border-green-200 dark:border-green-900">
                <span className="text-lg font-semibold text-gray-900 dark:text-white">Total Amount Paid</span>
                <span className="text-2xl font-bold text-green-600">INR {receiptData.amount.toFixed(2)}</span>
              </div>
            </div>

            {receiptData.payerUpi !== 'Not provided' && (
              <div className="mb-8 pb-8 border-b border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Paid From UPI ID</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white break-all">{receiptData.payerUpi}</p>
              </div>
            )}

            <div className="bg-blue-50 dark:bg-gray-900 border border-blue-200 dark:border-gray-700 rounded-lg p-4 mb-8 text-center">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                Please keep this receipt for your records. A confirmation email will be sent to your registered email address.
              </p>
            </div>

            <style>{`
              @media print {
                body {
                  background: white;
                  margin: 0;
                  padding: 0;
                }
                .no-print {
                  display: none;
                }
                .print-only {
                  display: block;
                }
              }
              @media screen {
                .print-only {
                  display: none;
                }
              }
            `}</style>
          </div>

          <div className="no-print mt-8 flex gap-4 justify-center">
            <button
              onClick={handleDownloadPDF}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
            >
              <Download className="w-5 h-5" />
              Download PDF
            </button>
            <button
              onClick={() => navigate('/my-applications')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              <Home className="w-5 h-5" />
              Back to Applications
            </button>
          </div>
        </div>
      ) : (
        <div className="max-w-3xl mx-auto">
          <button
            onClick={() => navigate('/my-applications')}
            className="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Applications
          </button>

          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 md:p-10">
            <div className="text-center mb-10">
              <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center shadow-lg">
                <CreditCard className="w-8 h-8" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Complete Your Payment</h1>
              <p className="text-gray-500 dark:text-gray-400">Submit your payment details to activate your services</p>
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-12 h-12 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin mb-4"></div>
                <p className="text-gray-500">Loading payment details...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-gray-900 dark:to-gray-800 rounded-xl border border-blue-200 dark:border-gray-700 p-6">
                  <h2 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                    Selected Applications ({apps.length})
                  </h2>
                  <div className="space-y-3">
                    {apps.map((app) => {
                      const effectiveFee = Number(app.Effective_fee || 0);

                      return (
                        <div key={app.Id || app.ApplicationId} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-100 dark:border-gray-700 hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900 dark:text-white">{app.ServiceName}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Application ID: {app.Id || app.ApplicationId}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-green-600">INR {effectiveFee.toFixed(2)}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-6 pt-6 border-t border-blue-200 dark:border-gray-700">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-gray-900 dark:text-white">Total Amount to Pay:</span>
                      <span className="text-3xl font-bold text-green-600">INR {totalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {paymentSetting && (
                  <div className="bg-gradient-to-br from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-900 rounded-xl p-6 text-white shadow-lg">
                    <p className="font-semibold mb-1 text-lg">Pay To</p>
                    <p className="text-blue-100 mb-4">{paymentSetting.Account_name || 'RTO Bharat'}</p>
                    {paymentSetting.Upi_id && (
                      <div className="bg-white bg-opacity-20 rounded-lg p-3 mb-4">
                        <p className="text-blue-100 text-sm mb-1">UPI ID</p>
                        <p className="font-mono text-lg font-semibold">{paymentSetting.Upi_id}</p>
                      </div>
                    )}
                    {((useDynamicQr && dynamicQrUrl) || paymentSetting.Qr_image || paymentQrImage) && (
                      <div className="bg-white rounded-lg p-3 inline-block">
                        <img
                          src={(useDynamicQr && dynamicQrUrl) || getQrUrl(paymentSetting.Qr_image)}
                          alt="Payment QR"
                          className="w-48 h-48 rounded-lg object-cover"
                          onError={() => {
                            if (useDynamicQr && dynamicQrUrl) {
                              setUseDynamicQr(false);
                              return;
                            }
                            setQrLoadError(true);
                          }}
                        />
                      </div>
                    )}
                    {qrLoadError && (
                      <p className="mt-3 text-sm text-red-200">
                        QR image could not load. Saved path: {paymentSetting.Qr_image}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Your UPI ID <span className="text-gray-500">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. user@paytm"
                    value={upiId}
                    onChange={(e) => setUpiId(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Leave empty if you want to scan the QR code manually
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg p-4">
                    <p className="text-red-600 dark:text-red-200 font-medium">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting || loading || !apps.length}
                  className="w-full py-4 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin"></div>
                      Processing...
                    </span>
                  ) : (
                    `Submit Payment - INR ${totalAmount.toFixed(2)}`
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicPayment;
